import { api } from "@/api/client"
import { updateUserCity } from "@/services/profile"
import { DigitalPassport } from "@/types/battle"

async function getCities(): Promise<unknown> {
  const { data } = await api.get("/city")
  return data
}

export type CityRecord = {
  city_id?: number
  title?: string
  polygon?: unknown
}

export type CityPolygonPoint = {
  latitude: number
  longitude: number
}

/** city_id из игрового паспорта (корень или вложенный city). */
export function resolvePassportCityId(
  passport: DigitalPassport | null | undefined
): number | null {
  if (!passport) return null
  const raw = passport.city_id ?? passport.city?.city_id
  if (raw == null) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

export function normalizeCityTitle(value: string): string {
  return String(value || "")
    .toLowerCase()
    .replace(/[ё]/g, "е")
    .replace(/[^\p{L}\p{N}]+/gu, "")
}

/** Город из приложения daksby (AsyncStorage / профиль). */
export function resolveAppCity(
  appCity: string | null | undefined,
  profile?: { city?: { title?: string } | string | null } | null
): string | null {
  if (appCity?.trim()) return appCity.trim()
  const profileCity = profile?.city
  if (typeof profileCity === "string" && profileCity.trim()) {
    return profileCity.trim()
  }
  if (
    profileCity &&
    typeof profileCity === "object" &&
    "title" in profileCity &&
    typeof profileCity.title === "string"
  ) {
    return profileCity.title.trim()
  }
  return null
}

export function resolveProfileCityId(
  profile?: {
    city_id?: number
    city?: { city_id?: number; title?: string } | string | null
  } | null
): number | null {
  const cityObj =
    profile?.city && typeof profile.city === "object" ? profile.city : null
  const raw = profile?.city_id ?? cityObj?.city_id
  if (raw == null) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

export function resolveCityIdByTitle(
  cities: CityRecord[],
  preferredCityName?: string | null
): number | null {
  const sourceName = String(preferredCityName || "").trim()
  if (!sourceName) return null

  const sourceNormalized = normalizeCityTitle(sourceName)
  if (!sourceNormalized) return null

  const exactMatch = cities.find(
    (cityItem) => normalizeCityTitle(String(cityItem.title ?? "")) === sourceNormalized
  )
  if (exactMatch?.city_id != null) return Number(exactMatch.city_id)

  const partialMatch = cities.find((cityItem) => {
    const normalizedTitle = normalizeCityTitle(String(cityItem.title ?? ""))
    return (
      normalizedTitle.includes(sourceNormalized) ||
      sourceNormalized.includes(normalizedTitle)
    )
  })

  return partialMatch?.city_id != null ? Number(partialMatch.city_id) : null
}

export function resolveCityTitleById(
  cities: CityRecord[],
  cityId: number | null | undefined
): string | null {
  if (cityId == null || !Number.isFinite(cityId)) return null
  const match = cities.find((cityItem) => Number(cityItem.city_id) === Number(cityId))
  const title = match?.title?.trim()
  return title || null
}

/**
 * city_id для игрового профиля при онбординге: сначала profile.city_id с бэка,
 * затем локальная строка из AsyncStorage.
 */
export function resolveGameCityId(
  appCity: string | null | undefined,
  profile: { city?: { title?: string; city_id?: number } | string | null; city_id?: number } | null | undefined,
  cities: CityRecord[]
): number | null {
  return (
    resolveProfileCityId(profile) ??
    resolveCityIdByTitle(cities, appCity) ??
    resolveCityIdByTitle(cities, resolveAppCity(appCity, profile))
  )
}

/** AsyncStorage «city» не совпадает с city_id в профиле пользователя. */
export function isAppCityMismatchWithProfile(
  appCity: string | null | undefined,
  profile: { city_id?: number; city?: { city_id?: number } | null } | null | undefined,
  cities: CityRecord[]
): boolean {
  const profileCityId = resolveProfileCityId(profile)
  if (profileCityId == null) return false
  const appCityId = resolveCityIdByTitle(cities, appCity)
  if (appCityId == null) return false
  return appCityId !== profileCityId
}

/** Название города из игрового паспорта (как на бэкенде). */
export function resolvePassportCityLabel(
  passport: DigitalPassport | null | undefined,
  cities?: CityRecord[] | null
): string | null {
  const title = passport?.city?.title?.trim()
  if (title) return title

  const cityId = resolvePassportCityId(passport)
  if (cities?.length && cityId != null) {
    return resolveCityTitleById(cities, cityId)
  }
  return null
}

/** Название города из GET /auth (profile.city_id), не из AsyncStorage. */
export function resolveProfileCityLabel(
  profile: { city?: { title?: string; city_id?: number } | string | null; city_id?: number } | null | undefined,
  cities?: CityRecord[] | null
): string | null {
  const profileCityId = resolveProfileCityId(profile)
  if (profileCityId != null && cities?.length) {
    const fromId = resolveCityTitleById(cities, profileCityId)
    if (fromId) return fromId
  }
  return resolveAppCity(null, profile)
}

/**
 * Город для UI «Хранители» — паспорт → profile.city_id → локальная строка.
 */
export function resolveGuardiansCityLabel(
  passport: DigitalPassport | null | undefined,
  appCity?: string | null | undefined,
  profile?: { city?: { title?: string } | string | null; city_id?: number } | null,
  cities?: CityRecord[] | null
): string {
  return (
    resolvePassportCityLabel(passport, cities) ??
    resolveProfileCityLabel(profile, cities) ??
    resolveAppCity(appCity, profile) ??
    "—"
  )
}

/**
 * Перед select-league: бэк читает profile.city_id, не AsyncStorage.
 * Если в профиле нет city_id, но город выбран в UI — PATCH /user/city + refresh.
 */
export async function ensureProfileCitySynced(params: {
  profile: { city_id?: number; city?: { city_id?: number } | null } | null | undefined
  appCity: string | null | undefined
  cities: CityRecord[]
  refreshProfile: () => Promise<unknown>
}): Promise<number | null> {
  const existingId = resolveProfileCityId(params.profile)
  if (existingId != null) return existingId

  const resolvedCityId = resolveGameCityId(
    params.appCity,
    params.profile,
    params.cities
  )
  if (resolvedCityId == null) return null

  await updateUserCity(resolvedCityId)
  const refreshed = await params.refreshProfile()
  return resolveProfileCityId(
    refreshed as { city_id?: number; city?: { city_id?: number } | null } | null
  ) ?? resolvedCityId
}

export function enrichPassportCity(
  passport: DigitalPassport,
  cities: CityRecord[]
): DigitalPassport {
  const cityId = resolvePassportCityId(passport)
  if (cityId == null) return passport

  const title =
    passport.city?.title?.trim() || resolveCityTitleById(cities, cityId)
  if (!title) return passport

  return {
    ...passport,
    city_id: cityId,
    city: { city_id: cityId, title },
  }
}

let citiesCache: CityRecord[] | null = null
let citiesPromise: Promise<CityRecord[]> | null = null

export async function loadGuardiansCities(force = false): Promise<CityRecord[]> {
  if (!force && citiesCache) return citiesCache
  if (!force && citiesPromise) return citiesPromise

  citiesPromise = getCities()
    .then((data) => {
      citiesCache = Array.isArray(data) ? data : []
      return citiesCache
    })
    .catch(() => {
      citiesCache = []
      return citiesCache
    })
    .finally(() => {
      citiesPromise = null
    })

  return citiesPromise
}

export function getCachedGuardiansCities(): CityRecord[] {
  return citiesCache ?? []
}

/** [[lat, lng], ...] или { latitude, longitude } → точки для карты. */
export function parseCityPolygon(polygon: unknown): CityPolygonPoint[] {
  if (!Array.isArray(polygon)) return []

  const points: CityPolygonPoint[] = []
  for (const item of polygon) {
    if (Array.isArray(item) && item.length >= 2) {
      const latitude = Number(item[0])
      const longitude = Number(item[1])
      if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
        points.push({ latitude, longitude })
      }
      continue
    }
    if (item && typeof item === "object") {
      const row = item as Record<string, unknown>
      const latitude = Number(row.latitude ?? row.lat)
      const longitude = Number(row.longitude ?? row.lng)
      if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
        points.push({ latitude, longitude })
      }
    }
  }
  return points
}

export function polygonCentroid(
  points: CityPolygonPoint[]
): CityPolygonPoint | null {
  if (points.length === 0) return null
  const sumLat = points.reduce((sum, point) => sum + point.latitude, 0)
  const sumLng = points.reduce((sum, point) => sum + point.longitude, 0)
  return {
    latitude: sumLat / points.length,
    longitude: sumLng / points.length,
  }
}

export function resolveCityRecord(
  cities: CityRecord[],
  passport: DigitalPassport | null | undefined,
  profile?: { city_id?: number; city?: { title?: string; city_id?: number } | null } | null,
  appCity?: string | null
): CityRecord | null {
  const cityId =
    resolvePassportCityId(passport) ?? resolveProfileCityId(profile ?? null)
  if (cityId != null) {
    return cities.find((city) => Number(city.city_id) === cityId) ?? null
  }

  const title =
    passport?.city?.title?.trim() ??
    resolveAppCity(appCity, profile) ??
    null
  if (!title) return null

  const resolvedId = resolveCityIdByTitle(cities, title)
  if (resolvedId == null) return null
  return cities.find((city) => Number(city.city_id) === resolvedId) ?? null
}
